import { useState } from 'react';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    // Honeypot check
    const honeypot = formData.get('company_alt');
    if (honeypot && typeof honeypot === 'string' && honeypot.trim() !== '') {
      setFormStatus('success');
      setFormMessage('感谢您的留言，我们会尽快回复！');
      form.reset();
      return;
    }

    // Remove honeypot from payload
    formData.delete('company_alt');

    setFormStatus('loading');

    try {
      const response = await fetch('https://readdy.ai/api/form/d9qodh6e3oq4jqumtnvg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // ignore parse error
      }

      if (response.ok && parsed && (parsed as { code?: string }).code === 'OK') {
        setFormStatus('success');
        setFormMessage('感谢您的留言，我们会尽快回复！');
        form.reset();
      } else {
        const serverMsg = (parsed && (parsed as { meta?: { message?: string } }).meta?.message)
          || (parsed && (parsed as { message?: string }).message)
          || (parsed && (parsed as { meta?: { detail?: string } }).meta?.detail)
          || '提交失败，请稍后再试。';
        setFormStatus('error');
        setFormMessage(typeof serverMsg === 'string' ? serverMsg : '提交失败，请稍后再试。');
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请检查连接后重试。');
    }
  };

  const schemaData = [
    {
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      name: '关于我们 - 福利社导航',
      description: '了解福利社导航的品牌故事、信誉保障和联系方式。',
      url: 'https://fulishe-dh.com/about',
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'https://fulishe-dh.com/' },
        { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://fulishe-dh.com/about' },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: '福利社导航',
      url: 'https://fulishe-dh.com/',
      description: '福利社导航为您汇聚全网最新最全的福利资源入口',
      email: 'contact@fulishe-dh.com',
      foundingDate: '2024',
    },
  ];

  return (
    <>
      <SEOHead
        title="关于我们"
        description="了解福利社导航的品牌故事、信誉保障和联系方式。福利社导航致力于为中文互联网用户提供最优质的福利资源导航服务。"
        keywords="福利社导航,关于福利社导航,福利社品牌介绍,福利社联系方式"
        canonicalUrl="https://fulishe-dh.com/about"
        schemaData={schemaData}
      />

      <main className="pt-16">
        {/* Page Header */}
        <section className="py-10 md:py-14 px-4 md:px-6 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />
            <h1 className="text-xl md:text-3xl font-bold text-foreground-950 font-heading mt-2">
              关于福利社导航
            </h1>
            <p className="text-sm text-foreground-600 mt-3 leading-relaxed max-w-2xl">
              致力于为中文互联网用户打造最便捷、最全面的福利资源导航平台
            </p>
          </div>
        </section>

        {/* Brand Introduction */}
        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
              <div>
                <h2 className="text-lg md:text-2xl font-bold text-foreground-950 font-heading mb-4">
                  品牌故事
                </h2>
                <div className="space-y-3 text-sm text-foreground-700 leading-relaxed">
                  <p>
                    福利社导航成立于2024年，由一群热爱分享、乐于助人的互联网从业者共同创建。我们的初衷很简单——帮助更多人轻松找到优质的福利资源，让每个人都能享受到互联网带来的便利和实惠。
                  </p>
                  <p>
                    在信息爆炸的时代，优质的福利资源往往被淹没在海量信息中。福利社导航通过人工精选+智能推荐的方式，为您筛选出真正有价值的福利内容，让您不再错过任何一个好福利。
                  </p>
                  <p>
                    我们坚信，好的资源应该被更多人知道。福利社导航坚持每日更新，不断收录新的优质福利站点和资源，致力于成为中文互联网最值得信赖的福利资源导航平台。
                  </p>
                </div>
              </div>
              <div className="relative h-64 md:h-80 rounded-lg overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20minimalist%20office%20with%20team%20collaborating%20around%20a%20warm%20wooden%20table%2C%20natural%20light%20streaming%20through%20large%20windows%2C%20creative%20workspace%20with%20plants%20and%20books%2C%20warm%20amber%20and%20cream%20color%20palette%2C%20cozy%20and%20inviting%20atmosphere%2C%20professional%20yet%20relaxed%20editorial%20photography&width=800&height=600&seq=about-brand-2026&orientation=landscape"
                  alt="福利社导航团队"
                  title="福利社导航 - 品牌故事"
                  className="w-full h-full object-cover object-top rounded-lg"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Mission & Values */}
        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-lg md:text-2xl font-bold text-foreground-950 font-heading mb-8 text-center">
              我们的使命与价值观
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              {[
                { icon: 'ri-compass-3-line', title: '使命', desc: '让每个人都能轻松发现和获取优质的福利资源，消除信息不对称，共享互联网红利。' },
                { icon: 'ri-eye-line', title: '愿景', desc: '成为中文互联网最值得信赖的福利资源导航平台，服务千万用户。' },
                { icon: 'ri-shield-check-line', title: '价值观', desc: '用户至上，品质为先。每一个收录的资源都经过严格审核，确保安全可靠。' },
              ].map((item, idx) => (
                <div key={idx} className="bg-background-50 border border-background-200/70 rounded-lg p-5 text-center">
                  <div className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center mx-auto mb-4">
                    <i className={`${item.icon} text-primary-600 text-xl`}></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-950 mb-2">{item.title}</h3>
                  <p className="text-xs text-foreground-600 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Reputation / Trust */}
        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-lg md:text-2xl font-bold text-foreground-950 font-heading mb-8 text-center">
              信誉保障
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {[
                { icon: 'ri-shield-check-line', title: '严格审核', desc: '每个资源入口都经过人工审核，确保来源正规、内容可靠' },
                { icon: 'ri-refresh-line', title: '每日更新', desc: '编辑团队7×24小时追踪福利动态，确保信息及时准确' },
                { icon: 'ri-user-heart-line', title: '用户至上', desc: '以用户体验为核心，界面简洁直观，查找资源省时省力' },
                { icon: 'ri-lock-line', title: '隐私保护', desc: '不收集用户个人信息，不追踪用户行为，安全放心使用' },
              ].map((item, idx) => (
                <div key={idx} className="text-center">
                  <div className="w-12 h-12 rounded-full bg-accent-100 flex items-center justify-center mx-auto mb-3">
                    <i className={`${item.icon} text-accent-600 text-xl`}></i>
                  </div>
                  <h4 className="text-sm font-semibold text-foreground-950 mb-1.5">{item.title}</h4>
                  <p className="text-xs text-foreground-600 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
              {/* Contact Info */}
              <div>
                <h2 className="text-lg md:text-2xl font-bold text-foreground-950 font-heading mb-6">
                  联系我们
                </h2>
                <div className="space-y-5">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-primary-600"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground-950 mb-1">电子邮箱</h4>
                      <p className="text-sm text-foreground-600">contact@fulishe-dh.com</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-time-line text-accent-600"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground-950 mb-1">工作时间</h4>
                      <p className="text-sm text-foreground-600">周一至周五 9:00 - 18:00</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-secondary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-chat-smile-2-line text-secondary-600"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground-950 mb-1">反馈建议</h4>
                      <p className="text-sm text-foreground-600">欢迎通过右侧表单提交资源推荐或意见建议</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Form */}
              <div>
                <div className="bg-background-50 border border-background-200/70 rounded-lg p-5">
                  <h3 className="text-base font-semibold text-foreground-950 mb-4">
                    给我们留言
                  </h3>
                  <form
                    onSubmit={handleSubmit}
                    data-readdy-form
                    className="space-y-4"
                  >
                    {/* Honeypot */}
                    <div className="field-website_alt">
                      <input
                        type="text"
                        name="company_alt"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                      />
                    </div>

                    <div>
                      <label htmlFor="about-name" className="block text-xs font-medium text-foreground-700 mb-1.5">
                        您的称呼 <span className="text-accent-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="about-name"
                        name="name"
                        required
                        className="w-full px-3 py-2.5 bg-background-50 border border-background-300/60 rounded-md text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的称呼"
                      />
                    </div>

                    <div>
                      <label htmlFor="about-email" className="block text-xs font-medium text-foreground-700 mb-1.5">
                        电子邮箱 <span className="text-accent-500">*</span>
                      </label>
                      <input
                        type="email"
                        id="about-email"
                        name="email"
                        required
                        className="w-full px-3 py-2.5 bg-background-50 border border-background-300/60 rounded-md text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>

                    <div>
                      <label htmlFor="about-subject" className="block text-xs font-medium text-foreground-700 mb-1.5">
                        留言主题
                      </label>
                      <input
                        type="text"
                        id="about-subject"
                        name="subject"
                        className="w-full px-3 py-2.5 bg-background-50 border border-background-300/60 rounded-md text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="如：资源推荐、问题反馈"
                      />
                    </div>

                    <div>
                      <label htmlFor="about-message" className="block text-xs font-medium text-foreground-700 mb-1.5">
                        留言内容 <span className="text-accent-500">*</span>
                      </label>
                      <textarea
                        id="about-message"
                        name="message"
                        required
                        maxLength={500}
                        rows={4}
                        className="w-full px-3 py-2.5 bg-background-50 border border-background-300/60 rounded-md text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                        placeholder="请输入您的留言内容，不超过500字"
                      ></textarea>
                    </div>

                    {formStatus !== 'idle' && (
                      <div
                        className={`text-xs px-3 py-2 rounded-md ${
                          formStatus === 'success'
                            ? 'bg-green-50 text-green-700 border border-green-200'
                            : formStatus === 'error'
                            ? 'bg-red-50 text-red-700 border border-red-200'
                            : 'bg-background-100 text-foreground-600'
                        }`}
                      >
                        {formStatus === 'loading' ? '正在提交...' : formMessage}
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={formStatus === 'loading'}
                      className="w-full bg-primary-500 hover:bg-primary-600 disabled:opacity-60 text-background-50 py-2.5 rounded-md text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
                    >
                      {formStatus === 'loading' ? '提交中...' : '提交留言'}
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-lg md:text-2xl font-bold text-foreground-950 font-heading mb-3">
              开始探索福利资源
            </h2>
            <p className="text-sm text-foreground-600 mb-6">
              每天都有新的优质福利资源被收录，现在就来看看吧
            </p>
            <a
              href="/"
              className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 px-6 py-3 rounded-lg font-medium text-sm transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-home-4-line"></i>
              返回首页
            </a>
          </div>
        </section>
      </main>
    </>
  );
}